// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import ConfigHandler from '../../loaders/handlers/ConfigHandler';
import SteadyState from '../../animationpack/state/SteadyState';
import ClipSource from '../../animationpack/blendtree/ClipSource';
import ManagedTransformSource from '../../animationpack/blendtree/ManagedTransformSource';
import BinaryLerpSource from '../../animationpack/blendtree/BinaryLerpSource';
import FrozenClipSource from '../../animationpack/blendtree/FrozenClipSource';
import ObjectUtils from '../../util/ObjectUtils';

/**
 * Handler for loading animation states into engine
 */
class AnimationStateHandler extends ConfigHandler {

  /**
   * @param {...*} args {@link ConfigHandler} constructor arguments.
   */
  constructor(...args) {
    super(...args);
  }

  /**
   * Creates an empty animation state
   * @param {string} ref
   * @returns {SteadyState}
   * @private
   */
  _create(ref) {
    const steadyState = new SteadyState();
    this._objects.set(ref, steadyState);
    return steadyState;
  }

  /**
   * Adds/updates/removes an animation state
   * @param {string} ref
   * @param {Object} config
   * @param {Object} options
   * @returns {Promise} Resolves with the updated animation state or null if removed
   */
  _update(ref, config, options) {
    const that = this;
    return ConfigHandler.prototype._update.call(this, ref, config, options).then((state) => {
      if (!state) { return; }
      state._name = config.name;
      state.id = config.id;
      state._transitions = ObjectUtils.deepClone(config.transitions);

      return that._parseClipSource(config.clipSource, state._sourceTree, options).then((source) => {
        state._sourceTree = source;
        return state;
      });
    });
  }

  /**
   * Updates or creates clipSource to put on animation state
   * @param {Object} config
   * @param {ClipSource} [clipSource]
   * @param {Object} [options]
   * @returns {Promise} resolved with updated clip source
   */
  _parseClipSource(config, clipSource, options) {
    switch (config.type) {
      case 'Clip':
        if (config.clipRef) {
          return this.loadObject(config.clipRef, options).then((clip) => {
            if (clipSource && (clipSource instanceof ClipSource)) {
              clipSource._clip = clip;
              clipSource.setFilter(config.filter, config.channels);
            } else {
              clipSource = new ClipSource(clip, config.filter, config.channels, this.world);
            }

            if (config.loopCount !== undefined) {
              clipSource._clipInstance._loopCount = +config.loopCount;
            }

            if (config.timeScale !== undefined) {
              clipSource._clipInstance._timeScale = config.timeScale;
            }

            let minTime = Infinity;
            for (let i = 0; i < clip._channels.length; i++) {
              const channel = clip._channels[i];
              for (let j = 0; j < channel._times.length; j++) {
                const time = channel._times[j];
                if (time < minTime) { minTime = time; }
              }
            }
            clipSource._startTime = config.startTime !== undefined ? Math.max(config.startTime, minTime) : (minTime === Infinity ? 0 : minTime);

            return clipSource;
          });
        } else {
          return Promise.resolve(null);
        }
      case 'Managed':
        if (!clipSource || !(clipSource instanceof ManagedTransformSource)) {
          clipSource = new ManagedTransformSource();
        }
        if (config.clipRef) {
          return this.loadObject(config.clipRef, options).then((clip) => {
            clipSource.initFromClip(clip, config.filter, config.channels);
            return clipSource;
          });
        }
        return Promise.resolve(clipSource);

      case 'Lerp':
        // TODO reuse object like the other parsers
        var promises = [
          this._parseClipSource(config.clipSourceA, null, options),
          this._parseClipSource(config.clipSourceB, null, options),
        ];
        return Promise.all(promises).then((clipSources) => {
          clipSource = new BinaryLerpSource(clipSources[0], clipSources[1]);
          if (config.blendWeight) {
            clipSource.blendWeight = config.blendWeight;
          }
          return clipSource;
        });
      case 'Frozen':
        return this._parseClipSource(config.clipSource).then((subClipSource) => {
          if (!clipSource || !(clipSource instanceof FrozenClipSource)) {
            clipSource = new FrozenClipSource(subClipSource, config.frozenTime || 0.0);
          } else {
            clipSource._source = subClipSource;
            clipSource._time = config.frozenTime || 0.0;
          }
          return clipSource;
        });
      default:
        console.error('[Sumerian] Unable to parse clip source');
        return Promise.resolve();
    }
  }
}

ConfigHandler._registerClass('animstate', AnimationStateHandler);

export default AnimationStateHandler;
